import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import api, { getTenantSubdomain } from "../services/api";
import { formatCurrency } from "../utils/formatters";
import { 
  Package, 
  ShoppingCart, 
  BarChart3, 
  Archive, 
  ShieldCheck, 
  Users, 
  Layers, 
  Check, 
  ArrowRight,
  HelpCircle
} from "lucide-react";

export default function LandingPage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const subdomain = getTenantSubdomain();
    const hostname = window.location.hostname.toLowerCase();
    const reservedSubdomains = ["erp", "www", "app", "api", "admin", "shop", "staging"];
    
    // Only auto-redirect to /login if it's a specific tenant subdomain (e.g. acme.erp.shreesavriyainnovations.com)
    if (
      subdomain &&
      !reservedSubdomains.includes(subdomain) &&
      hostname !== "localhost" &&
      hostname !== "127.0.0.1"
    ) {
      navigate("/login", { replace: true });
      return;
    }

    if (user) {
      if (role === "super_admin") {
        navigate("/platform", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [user, role, navigate]);

  const [fetchedPlans, setFetchedPlans] = useState<any[]>([]);

  useEffect(() => {
    async function loadPublicPlans() {
      try {
        const res = await api.get("/api/plans");
        if (res.data?.plans && Array.isArray(res.data.plans) && res.data.plans.length > 0) {
          setFetchedPlans(res.data.plans);
        }
      } catch (err) {
        console.warn("Failed to fetch public plans, using defaults:", err);
      }
    }
    loadPublicPlans();
  }, []);

  const defaultPlans = [
    {
      name: "Starter Plan",
      price: formatCurrency(2499, "INR"),
      period: "/month",
      desc: "Perfect for single-location retail shops and micro-businesses.",
      features: [
        "Single Store / Warehouse",
        "Core POS & Sales Billing",
        "Inventory Catalog & Barcodes",
        "Basic Customer Profiles",
        "Standard Thermal Receipt Printing",
      ],
      cta: "Contact Sales",
      popular: false,
    },
    {
      name: "Growth Plan",
      price: formatCurrency(5999, "INR"),
      period: "/month",
      desc: "Designed for expanding businesses with multi-location operations.",
      features: [
        "Multi-Warehouse Inventory",
        "Double-entry Core Accounting",
        "Purchase Orders & Supplier Ledgers",
        "Expense Tracking & Basic Payroll",
        "Thermal & Standard A4 Print Formats",
        "Dynamic Permission Control Matrix",
      ],
      cta: "Start Free Trial",
      popular: true,
    },
    {
      name: "Enterprise Plan",
      price: formatCurrency(14999, "INR"),
      period: "/month",
      desc: "Complete enterprise resource planning for large organizations.",
      features: [
        "Unlimited Store Locations",
        "Fixed Asset Tracking & Depreciation",
        "Advanced Financial Reporting (P&L, Balance Sheet)",
        "Audit Log Tracking & Compliance",
        "API Integration & Webhook Sync",
        "24/7 Dedicated Account Manager",
      ],
      cta: "Request Demo",
      popular: false,
    },
  ];

  const plans = fetchedPlans.length > 0
    ? fetchedPlans.map((p) => ({
        name: p.name,
        price: formatCurrency(Number(p.priceMonthly), p.priceCurrency || "INR"),
        period: "/month",
        desc: p.description || "Comprehensive ERP solution.",
        features: Array.isArray(p.enabledModules) && p.enabledModules.length > 0
          ? p.enabledModules.map((m: string) => m.toUpperCase() + " Module")
          : ["Multi-location support", "Full feature access"],
        cta: "Select Plan",
        popular: p.code === "growth",
      }))
    : defaultPlans;

  const erpFeatures = [
    {
      icon: Package,
      title: "Smart Inventory",
      desc: "Barcodes, automatic UOM conversions, real-time stock alert thresholds, and multi-warehouse sync.",
    },
    {
      icon: ShoppingCart,
      title: "POS & Billing",
      desc: "Generate sales orders, credit notes, thermal receipts, and trace customer balance ledgers instantly.",
    },
    {
      icon: BarChart3,
      title: "Double-Entry Accounting",
      desc: "Automated general ledger postings, fiscal periods, and real-time generation of Balance Sheets and P&Ls.",
    },
    {
      icon: Archive,
      title: "Fixed Asset Management",
      desc: "Maintain asset registers, configure depreciation profiles, and run automatic periodic write-downs.",
    },
    {
      icon: Users,
      title: "Team Permissions Matrix",
      desc: "Dynamic access matrix for cashiers, managers, and staff. Limit features to secure business operations.",
    },
    {
      icon: Layers,
      title: "Platform Scalability",
      desc: "Powered by isolated tenant databases, master credential key rings, and Redis session caching.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans antialiased">
      {/* Header / Navbar */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-primary-600 flex items-center justify-center text-white font-bold text-lg">
              S
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">SSI-ERP</span>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#features" className="hover:text-primary-600 transition-colors">Features</a>
            <a href="#pricing" className="hover:text-primary-600 transition-colors">Pricing Plans</a>
            <a href="#onboarding" className="hover:text-primary-600 transition-colors">Onboarding</a>
          </nav>

          <div className="flex items-center gap-4">
            {user ? (
              <Link
                to={role === "super_admin" ? "/platform" : "/dashboard"}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-all hover:shadow-md"
              >
                Go to Console
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm font-semibold text-slate-700 hover:text-primary-600 transition-colors"
                >
                  Sign In
                </Link>
                <a
                  href="#pricing"
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-all hover:shadow-md"
                >
                  Register Store
                </a>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden bg-gradient-to-b from-blue-50/70 via-white to-transparent">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 space-y-6 text-left">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-50 border border-primary-100 text-xs font-semibold text-primary-700">
              <ShieldCheck className="h-3.5 w-3.5" /> Isolated Database Security
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 leading-tight tracking-tight">
              Enterprise Grade ERP <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-indigo-600">
                Tailored for Retail SaaS
              </span>
            </h1>
            <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
              Scale your store operations, POS counters, double-entry financial ledger accounts, and depreciation schedules in a secure, isolated database sandbox.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <a
                href="#pricing"
                className="inline-flex items-center justify-center px-6 py-3 text-base font-bold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-all hover:shadow-lg"
              >
                Choose Pricing Plan
              </a>
              <a
                href="#features"
                className="inline-flex items-center justify-center px-6 py-3 text-base font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Explore Modules
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/3] rounded-2xl bg-gradient-to-tr from-primary-500 to-indigo-600 p-1.5 shadow-2xl relative">
              <div className="w-full h-full bg-slate-900 rounded-xl overflow-hidden flex flex-col justify-center items-center text-center p-8 border border-white/10">
                <Package className="h-16 w-16 text-primary-400 mb-4 animate-bounce" />
                <span className="text-lg font-bold text-white mb-2">Multi-Tenant Isolated MySQL</span>
                <p className="text-xs text-slate-400 max-w-xs">
                  Each shop receives its own private database container, shielding your critical sales data.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Section */}
      <section id="features" className="py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 text-center space-y-12">
          <div className="space-y-4 max-w-2xl mx-auto">
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">Full ERP Suite & Modules</h2>
            <p className="text-base text-slate-500 leading-relaxed">
              A comprehensive set of modules fully configured for retail management, pos counters, and deep financial compliance.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-left">
            {erpFeatures.map((f, idx) => {
              const Icon = f.icon;
              return (
                <div key={idx} className="p-6 border border-slate-200 rounded-xl hover:shadow-lg transition-shadow bg-slate-50/50">
                  <div className="h-12 w-12 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gradient-to-b from-transparent to-slate-100/50">
        <div className="max-w-7xl mx-auto px-6 text-center space-y-12">
          <div className="space-y-4 max-w-2xl mx-auto">
            <h2 className="text-3xl font-black text-slate-900 tracking-tight">Simple, Transparent Pricing</h2>
            <p className="text-base text-slate-500 leading-relaxed">
              Choose the package that matches your scale. All accounts are isolated and provisioned dynamically.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {plans.map((p, idx) => (
              <div 
                key={idx} 
                className={[
                  "bg-white border rounded-2xl p-8 flex flex-col justify-between text-left transition-all",
                  p.popular 
                    ? "border-primary-500 ring-2 ring-primary-500/20 shadow-xl relative lg:-translate-y-2 scale-100 lg:scale-105 z-10" 
                    : "border-slate-200 shadow-sm hover:shadow-md"
                ].join(" ")}
              >
                {p.popular && (
                  <span className="absolute top-0 right-8 -translate-y-1/2 px-3 py-1 bg-primary-600 text-[10px] font-bold text-white uppercase tracking-wider rounded-full">
                    Most Popular
                  </span>
                )}
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{p.name}</h3>
                  <p className="text-sm text-slate-500 mb-6">{p.desc}</p>
                  <div className="flex items-baseline gap-1 mb-8">
                    <span className="text-4xl font-extrabold text-slate-900">{p.price}</span>
                    <span className="text-sm text-slate-500">{p.period}</span>
                  </div>
                  <ul className="space-y-4 border-t border-slate-100 pt-6">
                    {p.features.map((f: string, fIdx: number) => (
                      <li key={fIdx} className="flex items-start gap-3">
                        <Check className="h-5 w-5 shrink-0 text-primary-600 mt-0.5" />
                        <span className="text-sm text-slate-600">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="pt-8">
                  <a
                    href="mailto:sales@platform.com?subject=SaaS Onboarding request"
                    className={[
                      "w-full inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-bold transition-all text-center",
                      p.popular
                        ? "bg-primary-600 hover:bg-primary-700 text-white shadow-md hover:shadow-lg"
                        : "bg-slate-100 hover:bg-slate-200 text-slate-800"
                    ].join(" ")}
                  >
                    {p.cta}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Onboarding Guide Callout */}
      <section id="onboarding" className="py-20 bg-white border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-8">
          <div className="h-14 w-14 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center mx-auto">
            <HelpCircle className="h-7 w-7" />
          </div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight">Need a custom onboarding?</h2>
          <p className="text-base text-slate-600 leading-relaxed">
            New shop registrations are processed and provisioned automatically. Once your billing account is validated, your isolated database cluster spins up immediately and your site URL is ready in under 60 seconds.
          </p>
          <div className="flex justify-center gap-4">
            <a
              href="mailto:support@platform.com?subject=Onboarding Question"
              className="inline-flex items-center justify-center px-6 py-2.5 text-sm font-semibold text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors"
            >
              Contact Support
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <span className="text-sm">&copy; {new Date().getFullYear()} SSI-ERP SaaS. All rights reserved.</span>
          <div className="flex gap-6 text-xs">
            <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
